import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import {
  DocumentIcon,
  ChevronRightIcon,
  SpinnerIcon,
  DotsVerticalIcon,
  EditIcon,
  TrashIcon,
  ChartBarIcon,
  EyeIcon,
  CopyIcon,
  CloudUploadIcon,
} from "../components/icons";
import {
  getOffers,
  duplicateOffer,
  deleteOffer,
  createOfferVersion,
  publishVersion,
  rejectDraft,
  getOfferVersions,
  type OfferVersion,
} from "../services/api";
import { OfferWithAnalytics, OfferStatus } from "../types";
import { useToast } from "../contexts/ToastContext";
import { VersionPickerModal } from "../components/offers/VersionPickerModal";

/**
 * Discriminated union representing the duplicate-offer flow.
 *
 * Replaces 3 overlapping useState (showVersionPicker, pickerVersions,
 * pickerBaseOfferId) + a separate `duplicating` flag, which could drift
 * out of sync. See review consolidated.md MEDIUM "OfferList trzyma 3
 * nakładające się useState".
 *
 * - idle: no duplicate in progress.
 * - loadingVersions: fetching `/offers/:id/versions` for the kebab row.
 * - pickingVersion: modal open, user choosing which published version to clone.
 * - duplicating: POST `/offers/:sourceId/duplicate` in flight.
 */
type DuplicateFlow =
  | { kind: "idle" }
  | { kind: "loadingVersions"; offerId: string }
  | { kind: "pickingVersion"; offerId: string; versions: OfferVersion[] }
  | { kind: "duplicating"; sourceId: string };

const OfferList: React.FC = () => {
  const [offers, setOffers] = useState<OfferWithAnalytics[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [flow, setFlow] = useState<DuplicateFlow>({ kind: "idle" });
  const [creatingVersion, setCreatingVersion] = useState<string | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    offerId: string;
    offerTitle: string;
  } | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const navigate = useNavigate();
  const { addToast } = useToast();

  // Returns true when this row's duplicate flow is busy (loading versions
  // or duplicating). Used to disable the kebab and show a row-level spinner
  // even when the dropdown menu has been closed by the user.
  const isRowBusy = (offerId: string): boolean => {
    if (flow.kind === "loadingVersions") return flow.offerId === offerId;
    if (flow.kind === "duplicating") return flow.sourceId === offerId;
    return false;
  };

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        setLoading(true);
        const fetchedOffers = await getOffers();
        setOffers(fetchedOffers);
      } catch (err) {
        console.error("Error fetching offers:", err);
        setError("Nie udało się załadować ofert");
      } finally {
        setLoading(false);
      }
    };

    fetchOffers();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      // Close dropdown if clicked outside of any dropdown area
      if (!target.closest(".dropdown-container")) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const executeDuplicate = async (sourceId: string, rowOfferId: string) => {
    setFlow({ kind: "duplicating", sourceId: rowOfferId });
    try {
      const duplicatedOffer = await duplicateOffer(sourceId);

      // Refresh offers list
      const updatedOffers = await getOffers();
      setOffers(updatedOffers);

      addToast({
        message: "Oferta została zduplikowana pomyślnie",
        type: "success",
      });

      // Redirect to edit the duplicated offer
      navigate(`/offers/${duplicatedOffer.id}/edit`);
    } catch (err) {
      console.error("Error duplicating offer:", err);
      addToast({
        message: "Nie udało się zduplikować oferty",
        type: "error",
      });
    } finally {
      setFlow({ kind: "idle" });
    }
  };

  const handleDuplicate = async (offerId: string) => {
    if (flow.kind !== "idle") return;

    setFlow({ kind: "loadingVersions", offerId });
    setOpenDropdown(null);

    try {
      const versions = await getOfferVersions(offerId);
      const publishedVersions = versions.filter(
        (v) => v.status === OfferStatus.PUBLISHED && v.publishedAt !== null,
      );

      if (publishedVersions.length >= 2) {
        setFlow({
          kind: "pickingVersion",
          offerId,
          versions: publishedVersions,
        });
      } else {
        await executeDuplicate(offerId, offerId);
      }
    } catch (err) {
      console.error("Error fetching offer versions:", err);
      // Toast must clearly state intent: we are NOT aborting — we are falling
      // back to duplicating the latest version. Otherwise user sees an error
      // toast while the action still succeeds (review consolidated.md
      // MEDIUM "Toast błędu + automatyczny fallback duplikacji").
      addToast({
        message:
          "Duplikuję z najnowszej wersji (nie udało się pobrać listy wersji)",
        type: "error",
      });
      // Fallback: duplicate from offer.id (pre-feature behaviour)
      await executeDuplicate(offerId, offerId);
    }
  };

  const handleVersionPickerConfirm = async (sourceVersionId: string) => {
    if (flow.kind !== "pickingVersion") {
      setFlow({ kind: "idle" });
      return;
    }
    const baseOfferId = flow.offerId;

    if (!sourceVersionId) {
      setFlow({ kind: "idle" });
      return;
    }
    await executeDuplicate(sourceVersionId, baseOfferId);
  };

  const handleVersionPickerCancel = () => {
    setFlow({ kind: "idle" });
  };

  const handleCreateVersion = async (offerId: string) => {
    try {
      setCreatingVersion(offerId);
      setOpenDropdown(null);

      const result = await createOfferVersion(offerId);

      // Odśwież listę ofert
      const updatedOffers = await getOffers();
      setOffers(updatedOffers);

      addToast({
        message: result.message,
        type: "success",
      });

      // Przekieruj do edycji draftu
      navigate(`/offers/${result.id}/edit`);
    } catch (err) {
      console.error("Error creating version:", err);
      addToast({
        message: "Nie udało się utworzyć wersji",
        type: "error",
      });
    } finally {
      setCreatingVersion(null);
    }
  };

  const handleDeleteClick = (offerId: string, offerTitle: string) => {
    setOpenDropdown(null);
    setDeleteConfirmation({ offerId, offerTitle });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmation) return;

    try {
      setDeleting(deleteConfirmation.offerId);
      await deleteOffer(deleteConfirmation.offerId);

      // Refresh the offers list
      const updatedOffers = await getOffers();
      setOffers(updatedOffers);

      addToast({
        message: "Oferta została usunięta",
        type: "success",
      });
    } catch (error) {
      console.error("Error deleting offer:", error);
      addToast({
        message: "Błąd podczas usuwania oferty",
        type: "error",
      });
    } finally {
      setDeleting(null);
      setDeleteConfirmation(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmation(null);
  };

  const handlePublishVersion = async (draftId: string) => {
    try {
      setOpenDropdown(null);

      const result = await publishVersion(draftId);

      // Odśwież listę ofert
      const updatedOffers = await getOffers();
      setOffers(updatedOffers);

      addToast({
        message: result.message || "Wersja została opublikowana",
        type: "success",
      });
    } catch (err) {
      console.error("Error publishing version:", err);
      addToast({
        message: "Nie udało się opublikować wersji",
        type: "error",
      });
    }
  };

  const handleRejectDraft = async (draftId: string) => {
    try {
      setOpenDropdown(null);

      const result = await rejectDraft(draftId);

      // Odśwież listę ofert
      const updatedOffers = await getOffers();
      setOffers(updatedOffers);

      addToast({
        message: result.message || "Wersja robocza została odrzucona",
        type: "success",
      });
    } catch (err) {
      console.error("Error rejecting draft:", err);
      addToast({
        message: "Nie udało się odrzucić wersji roboczej",
        type: "error",
      });
    }
  };

  const toggleDropdown = (offerId: string) => {
    setOpenDropdown(openDropdown === offerId ? null : offerId);
  };

  const formatDuration = (durationMs: number): string => {
    if (durationMs === 0) return "0s";

    const hours = Math.floor(durationMs / (60 * 60));
    const minutes = Math.floor((durationMs % (60 * 60)) / 60);
    const seconds = Math.floor(durationMs % 60);

    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (seconds > 0 && hours === 0) parts.push(`${seconds}s`);

    return parts.join(" ") || "0s";
  };

  if (loading) {
    return (
      <Layout showBackButton={true}>
        <div className="flex justify-center items-center py-20">
          <SpinnerIcon className="h-8 w-8 text-primary-600" />
          <span className="ml-2 text-lg text-neutral-600">
            Ładowanie ofert...
          </span>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout showBackButton={true}>
        <div className="text-center py-20 card">
          <div className="mx-auto h-12 w-12 bg-error-100 rounded-full flex items-center justify-center mb-4">
            <DocumentIcon className="h-6 w-6 text-error-600" />
          </div>
          <h3 className="text-lg font-semibold text-neutral-900">
            Error loading offers
          </h3>
          <p className="mt-1 text-sm text-neutral-600">{error}</p>
          <div className="mt-6">
            <button
              onClick={() => window.location.reload()}
              className="btn-primary"
            >
              Try Again
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const getIconColor = (status: OfferStatus) => {
    switch (status) {
      case OfferStatus.DRAFT:
        return "text-neutral-500";
      case OfferStatus.PUBLISHED:
        return "text-primary-600";
      case OfferStatus.ACCEPTED:
        return "text-success-600";
      case OfferStatus.REJECTED:
        return "text-error-600";
      default:
        return "text-neutral-500";
    }
  };

  const getIconBackgroundColor = (status: OfferStatus) => {
    switch (status) {
      case OfferStatus.DRAFT:
        return "bg-neutral-100";
      case OfferStatus.PUBLISHED:
        return "bg-primary-100";
      case OfferStatus.ACCEPTED:
        return "bg-success-100";
      case OfferStatus.REJECTED:
        return "bg-error-100";
      default:
        return "bg-neutral-100";
    }
  };

  const statusTooltips = {
    [OfferStatus.DRAFT]: "Szkic - w trakcie edycji",
    [OfferStatus.PUBLISHED]: "Opublikowana - dostępna publicznie",
    [OfferStatus.ACCEPTED]: "Zaakceptowana przez klienta",
    [OfferStatus.REJECTED]: "Odrzucona przez klienta",
  } as const;

  return (
    <Layout showBackButton={true}>
      <div className="flex justify-between items-center mb-8">
        <div></div>
        <Link
          to="/offers/create"
          className="bg-primary-600 hover:bg-primary-700 text-white font-semibold py-2 px-4 rounded-lg shadow-sm transition-colors duration-200"
        >
          Nowa oferta
        </Link>
      </div>

      <div className="card p-0">
        <ul className="divide-y divide-neutral-200" data-testid="offers-list">
          {offers.map((offer) => (
            <li
              key={offer.id}
              className="hover:bg-neutral-50 transition-colors"
              data-testid={`offer-row-${offer.id}`}
            >
              <div
                className="px-6 py-4 flex items-center justify-between"
                data-has-draft={offer.hasDraft ? "true" : "false"}
              >
                <Link
                  to={
                    offer.status === OfferStatus.DRAFT
                      ? `/offers/${offer.id}/edit`
                      : `/offers/public/${offer.mainOfferPublicToken || offer.publicToken}`
                  }
                  target={
                    offer.status === OfferStatus.DRAFT ? undefined : "_blank"
                  }
                  rel={
                    offer.status === OfferStatus.DRAFT
                      ? undefined
                      : "noopener noreferrer"
                  }
                  className="flex items-center flex-1 min-w-0"
                >
                  <div className="flex-shrink-0">
                    <div
                      className={`h-10 w-10 rounded-full ${getIconBackgroundColor(
                        offer.status
                      )} flex items-center justify-center`}
                      title={statusTooltips[offer.status]}
                      data-offer-status={offer.status}
                    >
                      <DocumentIcon
                        className={`h-6 w-6 ${getIconColor(offer.status)}`}
                      />
                    </div>
                  </div>
                  <div className="ml-4 flex-1 min-w-0 text-left">
                    <div className="flex items-center gap-2">
                      <p
                        className="text-sm text-neutral-900"
                        data-testid="offer-title"
                      >
                        <span className="font-semibold">{offer.title}</span> dla{" "}
                        <span className="font-semibold">
                          {offer.clientName}
                        </span>
                      </p>
                      {offer.hasDraft && (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800"
                          data-testid="draft-badge"
                        >
                          Wersja robocza
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <p className="text-sm text-neutral-500">
                        {(() => {
                          // Use checkInDate and checkOutDate from the offer
                          const checkInDate = offer.checkInDate;
                          const checkOutDate = offer.checkOutDate;

                          if (!checkInDate) {
                            // Fallback to creation date if no event dates
                            return `Utworzona ${new Date(offer.createdAt).toLocaleDateString("pl-PL")}`;
                          }

                          const fromDate = new Date(
                            checkInDate
                          ).toLocaleDateString("pl-PL");
                          const toDate = new Date(
                            checkOutDate
                          ).toLocaleDateString("pl-PL");

                          // If single day event, show only one date
                          if (fromDate === toDate) {
                            return fromDate;
                          }

                          // Multi-day event, show date range
                          return `${fromDate} - ${toDate}`;
                        })()}
                      </p>
                      {offer.status === OfferStatus.PUBLISHED &&
                        offer.latestVersionNumber &&
                        offer.latestVersionDate && (
                          <p className="text-xs text-neutral-400">
                            Wersja {offer.latestVersionNumber} (opublikowana{" "}
                            {new Date(
                              offer.latestVersionDate
                            ).toLocaleDateString("pl-PL", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                            )
                          </p>
                        )}
                    </div>
                  </div>
                </Link>
                <div className="flex items-center space-x-6 flex-shrink-0">
                  {/* Price column */}
                  <div className="text-right min-w-[80px]">
                    <p className="text-sm font-semibold text-neutral-900">
                      {offer.netValue.toLocaleString("pl-PL")} zł
                    </p>
                  </div>

                  {/* Duration column */}
                  <div className="text-right min-w-[100px]">
                    <span className="text-sm text-neutral-600">
                      {formatDuration(offer.total_session_duration || 0)}
                    </span>
                  </div>

                  {/* Actions column */}
                  <div className="flex items-center space-x-2">
                    {(offer.total_session_duration || 0) > 0 ? (
                      <Link
                        to={`/offers/${offer.id}/analytics`}
                        className="text-blue-600 hover:text-blue-800 transition-colors p-1 rounded-full hover:bg-blue-50"
                        title="Zobacz analitykę"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ChartBarIcon className="h-4 w-4" />
                      </Link>
                    ) : (
                      <div
                        className="text-neutral-400 opacity-50 p-1 rounded-full cursor-not-allowed pointer-events-none"
                        title="Brak czasu oglądania - analityka niedostępna"
                      >
                        <ChartBarIcon className="h-4 w-4" />
                      </div>
                    )}
                    <div className="relative dropdown-container">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleDropdown(offer.id);
                        }}
                        className={`p-1 text-neutral-400 hover:text-neutral-600 rounded-full hover:bg-neutral-100 transition-colors ${
                          isRowBusy(offer.id)
                            ? "opacity-50 cursor-wait"
                            : ""
                        }`}
                        disabled={isRowBusy(offer.id)}
                        aria-busy={isRowBusy(offer.id)}
                        data-testid="context-menu-button"
                      >
                        {isRowBusy(offer.id) ? (
                          <SpinnerIcon className="h-5 w-5" />
                        ) : (
                          <DotsVerticalIcon className="h-5 w-5" />
                        )}
                      </button>

                      {openDropdown === offer.id && (
                        <div
                          className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-neutral-200 z-50"
                          data-testid="context-menu"
                        >
                          <div className="py-1">
                            {/* CONDITIONAL MENU BASED ON DRAFT VERSION */}
                            {offer.hasDraft ? (
                              <>
                                {/* Edit draft version */}
                                <Link
                                  to={`/offers/${offer.draftVersion?.id}/edit`}
                                  className="flex items-center px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100 transition-colors"
                                  onClick={() => setOpenDropdown(null)}
                                  data-testid="edit-draft-button"
                                >
                                  <EditIcon className="h-4 w-4 mr-3" />
                                  Edytuj
                                </Link>
                                {/* View public offer */}
                                <Link
                                  to={`/offers/public/${offer.mainOfferPublicToken || offer.publicToken}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100 transition-colors"
                                  onClick={() => setOpenDropdown(null)}
                                  data-testid="view-public-offer-button"
                                >
                                  <EyeIcon className="h-4 w-4 mr-3" />
                                  Zobacz ofertę
                                </Link>
                                {/* Publish version */}
                                <button
                                  onClick={() =>
                                    handlePublishVersion(
                                      offer.draftVersion?.id || ""
                                    )
                                  }
                                  className="flex items-center w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100 transition-colors"
                                  data-testid="publish-version-button"
                                >
                                  <CloudUploadIcon className="h-4 w-4 mr-3" />
                                  Opublikuj wersję
                                </button>
                                {/* Reject draft */}
                                <button
                                  onClick={() =>
                                    handleRejectDraft(
                                      offer.draftVersion?.id || ""
                                    )
                                  }
                                  className="flex items-center w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50 transition-colors"
                                  data-testid="reject-draft-button"
                                >
                                  <TrashIcon className="h-4 w-4 mr-3" />
                                  Odrzuć wersję roboczą
                                </button>
                              </>
                            ) : (
                              <>
                                {/* Edit option - only show for draft offers without draft version */}
                                {offer.status === OfferStatus.DRAFT && (
                                  <Link
                                    to={`/offers/${offer.id}/edit`}
                                    className="flex items-center px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100 transition-colors"
                                    onClick={() => setOpenDropdown(null)}
                                    data-testid="edit-button"
                                  >
                                    <EditIcon className="h-4 w-4 mr-3" />
                                    Edytuj
                                  </Link>
                                )}
                                {/* Create Version - only for published offers without draft version */}
                                {offer.status === OfferStatus.PUBLISHED && (
                                  <button
                                    onClick={() =>
                                      handleCreateVersion(offer.id)
                                    }
                                    className="flex items-center w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100 transition-colors"
                                    disabled={creatingVersion === offer.id}
                                    title="Kolejna wersja tej samej oferty"
                                    data-testid="create-version-button"
                                  >
                                    <EditIcon className="h-4 w-4 mr-3" />
                                    Nowa wersja
                                  </button>
                                )}
                              </>
                            )}
                            {/* Duplicate option - show for all offers */}
                            <button
                              onClick={() => handleDuplicate(offer.id)}
                              className="flex items-center w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100 transition-colors"
                              disabled={isRowBusy(offer.id)}
                              title="Tworzy niezależną, nową ofertę"
                              data-testid="duplicate-option"
                            >
                              <CopyIcon className="h-4 w-4 mr-3" />
                              Duplikuj
                            </button>
                            {/* Delete option - only show for draft offers without draft version */}
                            {offer.status === OfferStatus.DRAFT &&
                              !offer.hasDraft && (
                                <button
                                  onClick={() =>
                                    handleDeleteClick(offer.id, offer.title)
                                  }
                                  className="flex items-center w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50 transition-colors"
                                  data-testid="delete-option"
                                >
                                  <TrashIcon className="h-4 w-4 mr-3"></TrashIcon>
                                  Usuń
                                </button>
                              )}
                          </div>
                        </div>
                      )}
                    </div>
                    <Link
                      to={
                        offer.status === OfferStatus.PUBLISHED &&
                        (offer.mainOfferPublicToken || offer.publicToken)
                          ? `/offers/public/${offer.mainOfferPublicToken || offer.publicToken}`
                          : `/offers/${offer.id}/edit`
                      }
                      target={
                        offer.status === OfferStatus.PUBLISHED &&
                        (offer.mainOfferPublicToken || offer.publicToken)
                          ? "_blank"
                          : undefined
                      }
                      rel={
                        offer.status === OfferStatus.PUBLISHED &&
                        (offer.mainOfferPublicToken || offer.publicToken)
                          ? "noopener noreferrer"
                          : undefined
                      }
                    >
                      <ChevronRightIcon className="h-5 w-5 text-neutral-400" />
                    </Link>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {offers.length === 0 && (
        <div className="text-center py-20 card">
          <div className="mx-auto h-12 w-12 bg-primary-100 rounded-full flex items-center justify-center mb-4">
            <DocumentIcon className="h-6 w-6 text-primary-600" />
          </div>
          <h3 className="text-lg font-semibold text-neutral-900">
            No offers yet
          </h3>
          <p className="mt-1 text-sm text-neutral-600">
            Get started by creating your first B2B offer.
          </p>
          <div className="mt-6">
            <Link to="/offers/create" className="btn-primary">
              Create Offer
            </Link>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            data-testid="delete-confirmation-modal"
          >
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                  <TrashIcon className="h-6 w-6 text-red-600" />
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Usuń ofertę
                </h3>
              </div>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-500">
                Czy na pewno chcesz usunąć tę ofertę?
              </p>
              <p className="text-sm font-medium text-gray-900 mt-2">
                "{deleteConfirmation.offerTitle}"
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Ta operacja jest nieodwracalna.
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={handleDeleteCancel}
                disabled={deleting !== null}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                data-testid="cancel-delete-button"
              >
                Anuluj
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting !== null}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                data-testid="confirm-delete-button"
              >
                {deleting ? (
                  <>
                    <SpinnerIcon className="h-4 w-4 mr-2" />
                    Usuwanie...
                  </>
                ) : (
                  "Usuń"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Version Picker Modal — shown when offer has >= 2 PUBLISHED versions */}
      {flow.kind === "pickingVersion" && flow.versions.length >= 2 && (
        <VersionPickerModal
          versions={flow.versions}
          onConfirm={(sourceVersionId) => {
            handleVersionPickerConfirm(sourceVersionId);
          }}
          onCancel={handleVersionPickerCancel}
        />
      )}
    </Layout>
  );
};

export default OfferList;
